import React, { useState } from 'react';
import styled from 'styled-components';
import { useFormikContext, type FormikErrors } from 'formik';
import { Remove, Search } from '@mui/icons-material';
import { Grid, InputAdornment } from '@mui/material';
import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import type { InputTextProps, SupervisorFormValue } from './types';
import { PAGE_TEXT } from '../../lang';
import { SupervisorRoleTypes } from '../../../constants';
import {
  useGetStaffInfoByNetID,
  type SupervisorValue,
} from '../../../graphql/NominationForm';
import {
  TextField,
  IconButton,
  Checkbox,
  type AlertDetails,
  Alert,
  Select,
} from '../../../../../components';
import { titleTypeOptions } from '../../../../Application/components/ApplicationForm/AcademicReferee/options';

const pageText = PAGE_TEXT.SUPERVISOR_TEAM;
const ROLE_TYPE = pageText.ROLE_TYPE;

// keep the order align with the chief supervisor page
const SupervisorTextContent: InputTextProps[] = [
  {
    label: pageText.SUPERVISOR.NET_ID,
    name: 'netId',
  },
  {
    label: pageText.SUPERVISOR.INSTITUTION,
    name: 'institution',
  },
  {
    label: pageText.SUPERVISOR.DEPARTMENT,
    name: 'department',
  },
  {
    label: pageText.SUPERVISOR.GIVEN_NAME,
    name: 'givenName',
  },
  {
    label: pageText.SUPERVISOR.SURNAME,
    name: 'surname',
  },
  {
    label: pageText.SUPERVISOR.TITLE,
    name: 'title',
  },
  {
    label: pageText.SUPERVISOR.JOB_TITLE,
    name: 'jobTitle',
  },
  {
    label: pageText.SUPERVISOR.EMAIL,
    name: 'email',
  },
];

type SupervisorInfoProps = {
  index: number;
  isPendingCoSup: boolean;
  handleDeleteSupervisor: () => void;
};

export const SupervisorInfo: React.FunctionComponent<SupervisorInfoProps> = (
  props: SupervisorInfoProps,
) => {
  const {
    index: supervisorIndex,
    handleDeleteSupervisor,
    isPendingCoSup,
  } = props;
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const { values, handleChange, handleBlur, errors, touched, setFieldValue } =
    useFormikContext<SupervisorFormValue>();
  const roleType = values.supervisors[supervisorIndex].type;
  const polyuStaff =
    roleType === SupervisorRoleTypes.CHIEF_SUPERVISOR ||
    roleType === SupervisorRoleTypes.CO_SUPERVISOR;

  const supervisorType =
    ROLE_TYPE[
      values.supervisors[supervisorIndex].type as keyof typeof ROLE_TYPE
    ];

  const filteredSupervisorTextContent = polyuStaff
    ? SupervisorTextContent.filter(item => item.name !== 'institution')
    : SupervisorTextContent.filter(item => item.name !== 'netId');

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const handleSearchAndFill = async (): Promise<void> => {
    const searchId = values.supervisors[supervisorIndex].netId ?? '';
    try {
      const res = await useGetStaffInfoByNetID(client, searchId);
      if (res !== null) {
        const fieldsToUpdate = {
          department: res?.department ?? '',
          email: res?.email ?? '',
          givenName: res?.givenName ?? '',
          surname: res?.surname ?? '',
          isDrcChair: res?.rightDRC ?? false,
          jobTitle: res?.title ?? '', // the title from backend, which means post
        };

        for (const [field, value] of Object.entries(fieldsToUpdate)) {
          await setFieldValue(
            `supervisors[${supervisorIndex}].${field}`,
            value,
          );
        }
      } else {
        setAlertDetails({
          alertContent: 'No matching academic staff found.',
          alertType: 'warning',
        });
        setOpenAlert(true);
      }
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  return (
    <Container>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <div className="role-area">
        <h2>{supervisorType}</h2>
        <IconButton
          icon={<Remove />}
          onClick={handleDeleteSupervisor}
          disabled={isPendingCoSup}
        />
      </div>
      <Grid container columnSpacing={4} rowSpacing={2}>
        {filteredSupervisorTextContent.map((item, index) => {
          const fieldName = `supervisors.${supervisorIndex}.${
            item.name as string
          }`;

          const supervisorErrors = errors.supervisors as
            | Array<FormikErrors<SupervisorValue>>
            | undefined;

          const isNetIdSearch = item.name === 'netId';
          return (
            <Grid
              key={index}
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <Grid container spacing={2} alignItems="flex-start">
                {item.name === 'title' ? (
                  <Select
                    options={titleTypeOptions}
                    label={item.label}
                    name={fieldName}
                    value={values.supervisors[supervisorIndex][item.name]}
                    onChange={handleChange}
                    required
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    error={
                      touched.supervisors?.[supervisorIndex]?.[item.name] &&
                      Boolean(supervisorErrors?.[supervisorIndex]?.[item.name])
                    }
                    helperText={
                      touched.supervisors?.[supervisorIndex]?.[item.name] &&
                      supervisorErrors?.[supervisorIndex]?.[item.name]
                    }
                  />
                ) : (
                  <TextField
                    label={item.label}
                    name={fieldName}
                    value={values.supervisors[supervisorIndex][item.name]}
                    onChange={handleChange}
                    required
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    error={
                      touched.supervisors?.[supervisorIndex]?.[item.name] &&
                      Boolean(supervisorErrors?.[supervisorIndex]?.[item.name])
                    }
                    helperText={
                      touched.supervisors?.[supervisorIndex]?.[item.name] &&
                      supervisorErrors?.[supervisorIndex]?.[item.name]
                    }
                    InputProps={{
                      endAdornment: isNetIdSearch ? (
                        <InputAdornment position="end">
                          <IconButton
                            icon={<Search />}
                            onClick={handleSearchAndFill}
                            disabled={
                              values.supervisors[supervisorIndex].netId ===
                              undefined
                            }
                            color="primary"
                            title="Search by Net ID"
                          />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                )}
              </Grid>
            </Grid>
          );
        })}
        <Grid
          size={{
            md: 12,
          }}
        >
          <Checkbox
            value={
              values.supervisors[supervisorIndex].previousSuccessfulSupervision
            }
            label={pageText.SUPERVISOR.PREVIOUS_SUCCESSFUL_SUPERVISION}
            onChange={handleChange}
            name={`supervisors.${supervisorIndex}.previousSuccessfulSupervision`}
          />
          {polyuStaff && (
            <>
              <Checkbox
                value={values.supervisors[supervisorIndex].isDrcChair}
                label={pageText.SUPERVISOR.DRC_MEMBER}
                onChange={handleChange}
                name={`supervisors.${supervisorIndex}.isDrcChair`}
              />
              <Checkbox
                value={values.supervisors[supervisorIndex].isDoRPgS}
                label={pageText.SUPERVISOR.DORPGS_MEMBER}
                onChange={handleChange}
                name={`supervisors.${supervisorIndex}.isDoRPgS`}
              />
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  margin-top: 1rem;

  .MuiTextField-root {
    width: 100%;
    margin-bottom: 1rem;
  }

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;
    width: calc(100%);
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: row;
    padding: 0 1rem 0 0;

    label {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-right: 3rem;
    }
    input {
      margin-right: 0.5rem;
    }
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .role-area {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 1rem;

    h2 {
      margin-right: 1rem;
      font-size: 1rem;
      font-weight: 600;
    }

    button .MuiSvgIcon-root {
      background-color: #951d1d;
      color: #fff;
      border-radius: 50%;
      cursor: pointer;
      font-size: 1rem;
    }
  }
`;
